import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { createMerchant, CreateMerchantRequest } from "../../../../services/merchant/merchant_actions";
import { purple } from "../../../../theme/colors";
import { STATES_CODES } from "./constants";
interface CreateMerchantDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}


export default function CreateMerchantDialog({
  open,
  onClose,
  onSuccess,
}: CreateMerchantDialogProps) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [pincode, setPincode] = useState<string>("");
  const [location, setLocation] = useState("");
  const [state, setState] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useFeedback();

  const isValidMobile = (value: string): boolean => {
    const trimmed = value.trim().replace(/^(\+91|0)+/, "");
    return /^[6-9]\d{9}$/.test(trimmed);
  };

  const isValidPincode = (value: string): boolean => {
    return /^\d{6}$/.test(value.trim());
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showSnackbar("Merchant name is required");
      return;
    }
    if (!mobile.trim()) {
      showSnackbar("Mobile number is required");
      return;
    }
    if (!isValidMobile(mobile)) {
      showSnackbar("Please enter a valid 10-digit Indian mobile number");
      return;
    }
    if (!pincode.trim()) {
      showSnackbar("Pincode is required");
      return;
    }
    if (!isValidPincode(pincode)) {
      showSnackbar("Pincode must be exactly 6 digits");
      return;
    }
    if (!location.trim()) {
      showSnackbar("Location is required");
      return;
    }
    if (!state.trim()) {
      showSnackbar("State is required");
      return;
    }

    const pincodeNum = parseInt(pincode.trim(), 10);
    const mobileTrimmed = mobile.trim().replace(/^(\+91|0)+/, "");

    setIsLoading(true);
    try {
      const request: CreateMerchantRequest = {
        name: name.trim(),
        mobile_no: mobileTrimmed,
        pincode: pincodeNum,
        location: location.trim(),
        state: state,
      };
      
      await createMerchant(request);
      showSnackbar("Merchant created successfully!");
      onSuccess();
      onClose();
      // Reset form
      setName("");
      setMobile("");
      setPincode("");
      setLocation("");
      setState("");
    } catch (error) {
      showSnackbar("Failed to create merchant");
      console.error("Error creating merchant:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form
      setName("");
      setMobile("");
      setPincode("");
      setLocation("");
      setState("");
      onClose();
    }
  };

  const isFormValid =
    name.trim() !== "" &&
    mobile.trim() !== "" &&
    isValidMobile(mobile) &&
    pincode.trim() !== "" &&
    isValidPincode(pincode) &&
    location.trim() !== "" &&
    state.trim() !== "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="600">
          Create Merchant
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Merchant Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter merchant name"
                disabled={isLoading}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 12))}
                placeholder="10-digit mobile number (e.g. 9876543210)"
                disabled={isLoading}
                required
                error={mobile.trim() !== "" && !isValidMobile(mobile)}
                helperText={
                  mobile.trim() !== "" && !isValidMobile(mobile)
                    ? "Enter a valid 10-digit Indian mobile number (starting with 6-9)"
                    : undefined
                }
                inputProps={{ maxLength: 12 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pincode"
                value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit pincode"
                disabled={isLoading}
                required
                error={pincode.trim() !== "" && !isValidPincode(pincode)}
                helperText={
                  pincode.trim() !== "" && !isValidPincode(pincode)
                    ? "Pincode must be exactly 6 digits"
                    : undefined
                }
                inputProps={{ maxLength: 6 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={isLoading} required>
                <InputLabel id="merchant-state-label">State</InputLabel>
                <Select
                  labelId="merchant-state-label"
                  label="State"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  {STATES_CODES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter location"
                disabled={isLoading}
                required
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !isFormValid}
          sx={{ backgroundColor: purple }}
        >
          {isLoading ? "Creating..." : "Create Merchant"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
